import React, { useState, useRef, useEffect } from 'react'
import { toastError, toastSuccess, toastLoading, toastDismiss } from '../../utils/toastHelper'

import { EspacioDisponible } from '../../types'
import type { ConsultaDisponibilidad } from '../../types'

interface CampingFloorPlanProps {
  espacios: EspacioDisponible[]
  planoImageUrl: string // URL de la imagen del plano
  onReserve: (espacio: EspacioDisponible) => void
  searchQuery: ConsultaDisponibilidad | null
  className?: string
  showAvailabilityIndicators?: boolean // Mostrar indicadores de disponibilidad hoy
}

interface HoveredSpace {
  espacioId: string
  mouseX: number
  mouseY: number
}

// Determinar estado de disponibilidad del espacio en el rango de búsqueda
type DisponibilidadEstado = 'libre' | 'parcial' | 'ocupado' | 'no-buscado'

const getDisponibilidadEstado = (espacio: EspacioDisponible): DisponibilidadEstado => {
  // Si no tiene disponibilidad en el rango actual, está ocupado
  if (!espacio.disponible) {
    return 'ocupado'
  }

  // Si tiene disponibilidad parcial (menos días disponibles que el rango)
  // ✅ Usar typeof para evitar problemas con 0 (que es falsy pero válido)
  if (typeof espacio.total_dias_disponibles === 'number' && typeof espacio.total_dias_rango === 'number') {
    if (espacio.total_dias_disponibles < espacio.total_dias_rango) {
      return 'parcial'
    }
  }

  // Si está completamente disponible
  return 'libre'
}

// Colores según disponibilidad (para SVG)
const getColorEstado = (estado: DisponibilidadEstado): { fill: string; stroke: string } => {
  switch (estado) {
    case 'libre':
      return {
        fill: '#10b981', // green-500
        stroke: '#059669' // green-600
      }
    case 'parcial':
      return {
        fill: '#eab308', // yellow-400
        stroke: '#ca8a04' // yellow-600
      }
    case 'ocupado':
      return {
        fill: '#ef4444', // red-500
        stroke: '#dc2626' // red-600
      }
    case 'no-buscado':
      return {
        fill: '#6b7280', // gray-500
        stroke: '#4b5563' // gray-600
      }
  }
}

// Para tooltip HTML
const getColorClasses = (estado: DisponibilidadEstado): string => {
  switch (estado) {
    case 'libre':
      return 'bg-green-100 text-green-800'
    case 'parcial':
      return 'bg-yellow-100 text-yellow-800'
    case 'ocupado':
      return 'bg-red-100 text-red-800'
    case 'no-buscado':
      return 'bg-gray-100 text-gray-800'
  }
}

const getEstadoLabel = (estado: DisponibilidadEstado): string => {
  switch (estado) {
    case 'libre':
      return '✅ Disponible'
    case 'parcial':
      return '⚠️ Parcialmente disponible'
    case 'ocupado':
      return '❌ No disponible'
    case 'no-buscado':
      return 'Sin búsqueda'
  }
}

const CampingFloorPlan: React.FC<CampingFloorPlanProps> = ({
  espacios,
  planoImageUrl,
  onReserve,
  searchQuery,
  className = '',
  showAvailabilityIndicators = false
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 })
  const [hoveredSpace, setHoveredSpace] = useState<HoveredSpace | null>(null)
  const [hoveredSpaceId, setHoveredSpaceId] = useState<string | null>(null)

  // Calcular dimensiones de la imagen cuando se carga
  useEffect(() => {
    if (imgRef.current && imgRef.current.complete) {
      setImageDimensions({
        width: imgRef.current.naturalWidth,
        height: imgRef.current.naturalHeight
      })
    }
  }, [planoImageUrl])

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget
    setImageDimensions({
      width: img.naturalWidth,
      height: img.naturalHeight
    })
  }

  // Calcular escala responsiva
  const getScaleFactors = () => {
    if (!containerRef.current || !imageDimensions.width) {
      return { scaleX: 1, scaleY: 1 }
    }

    const containerWidth = containerRef.current.offsetWidth
    const scaleX = containerWidth / imageDimensions.width

    // Mantener aspecto
    const scaleY = scaleX

    return { scaleX, scaleY }
  }

  const handleSpaceHover = (espacio: EspacioDisponible, e: any) => {
    if (!containerRef.current) return

    // Calcular posición relativa al contenedor
    const rect = containerRef.current.getBoundingClientRect()
    const mouseX = e.clientX - rect.left
    const mouseY = e.clientY - rect.top

    setHoveredSpaceId(espacio.id)
    setHoveredSpace({
      espacioId: espacio.id,
      mouseX,
      mouseY
    })
  }

  const handleSpaceLeave = () => {
    setHoveredSpaceId(null)
    setHoveredSpace(null)
  }

  const handleSpaceClick = (espacio: EspacioDisponible) => {
    const estado = getDisponibilidadEstado(espacio)

    if (estado === 'ocupado') {
      toastError('Este espacio no está disponible en el rango seleccionado')
      return
    }

    onReserve(espacio)
  }

  // Filtrar espacios que tienen coordenadas de plano (circular, rectangular o polígono)
  // Solo aplicar filtro si hay una búsqueda activa, en HomePage mostramos todos
  const espaciosConCoordenadas = searchQuery
    ? espacios.filter(
        e => (e.plano_x !== null && e.plano_y !== null) || (e.plano_vertices && e.plano_vertices.length > 0)
      )
    : espacios

  if (espaciosConCoordenadas.length === 0) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <p className="text-gray-600">No hay espacios disponibles</p>
      </div>
    )
  }

  const { scaleX, scaleY } = getScaleFactors()

  return (
    <div className={`relative w-full ${className}`} ref={containerRef}>
      {/* Contenedor de la imagen del plano */}
      <div className="relative w-full overflow-visible rounded-2xl shadow-2xl border border-gray-200">
        {/* Imagen del plano */}
        <img
          ref={imgRef}
          src={planoImageUrl}
          alt="Plano del camping"
          onLoad={handleImageLoad}
          className="w-full h-auto block"
          draggable={false}
        />

        {/* Espacios mapeados sobre el plano */}
        <svg
          className="absolute inset-0 w-full h-full"
          style={{
            width: '100%',
            height: '100%',
            top: 0,
            left: 0
          }}
          viewBox={`0 0 ${imageDimensions.width} ${imageDimensions.height}`}
          preserveAspectRatio="xMidYMid slice"
        >
          {espaciosConCoordenadas.map((espacio) => {
            // ✅ Siempre usar la lógica completa que distingue libre/parcial/ocupado
            const estado = getDisponibilidadEstado(espacio)
            const colores = getColorEstado(estado)

            // Espacios circulares (camping)
            if (espacio.plano_radio && espacio.plano_radio > 0) {
              const isHovered = hoveredSpaceId === espacio.id
              // En modo búsqueda O indicadores de hoy: mostrar siempre, en homepage: solo en hover
              const showByDefault = !!searchQuery || showAvailabilityIndicators
              return (
                <g
                  key={`circle-${espacio.id}`}
                >
                  {/* Área invisible para detectar hover en toda el círculo */}
                  <circle
                    cx={espacio.plano_x!}
                    cy={espacio.plano_y!}
                    r={espacio.plano_radio}
                    fill="transparent"
                    stroke="none"
                    className="cursor-pointer"
                    onMouseEnter={(e) => handleSpaceHover(espacio, e)}
                    onMouseLeave={handleSpaceLeave}
                    onClick={() => handleSpaceClick(espacio)}
                  />
                  {/* Forma visible con relleno - solo si hay disponibilidad activa */}
                  {(showByDefault || isHovered) && showAvailabilityIndicators && (
                    <circle
                      cx={espacio.plano_x!}
                      cy={espacio.plano_y!}
                      r={espacio.plano_radio}
                      fill={colores.fill}
                      stroke={colores.stroke}
                      strokeWidth={isHovered ? "3" : "2"}
                      fillOpacity="0.4"
                      opacity={isHovered ? "1" : "0.8"}
                      pointerEvents="none"
                      className="transition-all duration-200"
                    />
                  )}
                </g>
              )
            }

            // Espacios rectangulares (cabañas)
            if (espacio.plano_ancho && espacio.plano_alto && espacio.plano_x && espacio.plano_y) {
              const isHovered = hoveredSpaceId === espacio.id
              // En modo búsqueda O indicadores de hoy: mostrar siempre, en homepage: solo en hover
              const showByDefault = !!searchQuery || showAvailabilityIndicators
              return (
                <g
                  key={`rect-${espacio.id}`}
                >
                  {/* Área invisible para detectar hover en todo el rectángulo */}
                  <rect
                    x={espacio.plano_x - espacio.plano_ancho / 2}
                    y={espacio.plano_y - espacio.plano_alto / 2}
                    width={espacio.plano_ancho}
                    height={espacio.plano_alto}
                    fill="transparent"
                    stroke="none"
                    className="cursor-pointer"
                    onMouseEnter={(e) => handleSpaceHover(espacio, e)}
                    onMouseLeave={handleSpaceLeave}
                    onClick={() => handleSpaceClick(espacio)}
                  />
                  {/* Forma visible con relleno - solo si hay disponibilidad activa */}
                  {(showByDefault || isHovered) && showAvailabilityIndicators && (
                    <rect
                      x={espacio.plano_x - espacio.plano_ancho / 2}
                      y={espacio.plano_y - espacio.plano_alto / 2}
                      width={espacio.plano_ancho}
                      height={espacio.plano_alto}
                      fill={colores.fill}
                      stroke={colores.stroke}
                      strokeWidth={isHovered ? "3" : "2"}
                      fillOpacity="0.4"
                      opacity={isHovered ? "1" : "0.8"}
                      pointerEvents="none"
                      className="transition-all duration-200"
                    />
                  )}
                </g>
              )
            }

            // Espacios poligonales (en cualquier orientación)
            if (espacio.plano_vertices && espacio.plano_vertices.length >= 3) {
              const isHovered = hoveredSpaceId === espacio.id
              // En modo búsqueda O indicadores de hoy: mostrar siempre, en homepage: solo en hover
              const showByDefault = !!searchQuery || showAvailabilityIndicators
              const pointsString = espacio.plano_vertices
                .map(v => `${v.x},${v.y}`)
                .join(' ')

              return (
                <g
                  key={`poly-${espacio.id}`}
                >
                  {/* Área invisible para detectar hover en todo el polígono */}
                  <polygon
                    points={pointsString}
                    fill="transparent"
                    stroke="none"
                    className="cursor-pointer"
                    onMouseEnter={(e) => handleSpaceHover(espacio, e)}
                    onMouseLeave={handleSpaceLeave}
                    onClick={() => handleSpaceClick(espacio)}
                  />
                  {/* Forma visible con relleno - solo si hay disponibilidad activa */}
                  {(showByDefault || isHovered) && showAvailabilityIndicators && (
                    <polygon
                      points={pointsString}
                      fill={colores.fill}
                      stroke={colores.stroke}
                      strokeWidth={isHovered ? "3" : "2"}
                      fillOpacity="0.4"
                      opacity={isHovered ? "1" : "0.8"}
                      pointerEvents="none"
                      className="transition-all duration-200"
                    />
                  )}
                </g>
              )
            }

            return null
          })}
        </svg>
      </div>

      {/* Tooltip al pasar mouse - solo si disponibilidad está activa */}
      {hoveredSpace && showAvailabilityIndicators && (
        <div
          className="absolute z-50 bg-white shadow-xl rounded-lg p-4 max-w-sm pointer-events-none border border-gray-100"
          style={{
            left: `${Math.max(8, hoveredSpace.mouseX + 12)}px`,
            top: `${Math.max(8, hoveredSpace.mouseY + 12)}px`,
            transform: 'translate(0, 0)'
          }}
        >
          {espaciosConCoordenadas
            .filter(e => e.id === hoveredSpace.espacioId)
            .map(espacio => {
              const estado = getDisponibilidadEstado(espacio)
              return (
                <div key={espacio.id} className="space-y-3">
                  <div>
                    <h4 className="font-bold text-gray-900 text-sm">{espacio.nombre}</h4>
                    <p className="text-xs text-gray-500">{espacio.codigo}</p>
                  </div>

                  <div className="space-y-1">
                    <p className="text-xs text-gray-600">
                      👥 <span className="font-semibold">{espacio.capacidad_maxima}</span> personas
                    </p>
                  </div>

                  {/* Solo mostrar estado de disponibilidad si está activo el checkbox */}
                  {showAvailabilityIndicators && (
                    <>
                      <div className={`flex items-center justify-center px-3 py-2 rounded-full text-xs font-semibold ${
                        estado === 'libre' ? 'bg-green-100 text-green-800' :
                        estado === 'parcial' ? 'bg-yellow-100 text-yellow-800' :
                        estado === 'ocupado' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {getEstadoLabel(estado)}
                      </div>

                      {estado === 'parcial' && espacio.total_dias_disponibles && (
                        <p className="text-xs text-gray-600 text-center">
                          {espacio.total_dias_disponibles} de {espacio.total_dias_rango} días disponibles
                        </p>
                      )}

                      {estado !== 'ocupado' && (
                        <p className="text-xs text-blue-600 font-semibold text-center mt-2">
                          👆 Click para reservar
                        </p>
                      )}
                    </>
                  )}
                </div>
              )
            })}
        </div>
      )}

      {/* Leyenda de disponibilidad - mostrar cuando están activos los indicadores */}
      {showAvailabilityIndicators && (
        <div className="mt-4 flex justify-center flex-wrap gap-4 text-xs">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-green-400 rounded-full border border-green-600"></div>
            <span className="text-gray-700">Disponible</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-yellow-400 rounded-full border border-yellow-600"></div>
            <span className="text-gray-700">Parcial</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-red-400 rounded-full border border-red-600"></div>
            <span className="text-gray-700">Ocupado</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default CampingFloorPlan
